import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import Image from 'next/image'
import { cn } from '@/lib/utils'
import { gsap, useGSAP, MOTION_QUERY } from '@/lib/gsap'

interface ImageRevealProps {
    src: string
    alt: string
    className?: string
    imgClassName?: string
    sizes?: string
    priority?: boolean
    parallax?: boolean
    reveal?: boolean
    children?: ReactNode
}

/**
 * Image that opens from the bottom up like a garage door as it scrolls into view,
 * with an optional scrubbed parallax drift.
 */
export const ImageReveal: FC<ImageRevealProps> = ({
    src,
    alt,
    className,
    imgClassName,
    sizes = '100vw',
    priority = false,
    parallax = false,
    reveal = true,
    children
}): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const innerRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        const element = ref.current
        const inner = innerRef.current

        if (!element || !inner) return

        const media = gsap.matchMedia()

        media.add(MOTION_QUERY, () => {
            if (reveal) {
                gsap.timeline({ scrollTrigger: { trigger: element, start: 'top 90%', once: true } })
                    .fromTo(
                        element,
                        { clipPath: 'inset(100% 0% 0% 0%)' },
                        { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.5, ease: 'expo.inOut' }
                    )
                    .from(inner, { scale: 1.22, duration: 2, ease: 'expo.out' }, 0.1)
            }

            if (parallax) {
                gsap.fromTo(
                    inner,
                    { yPercent: -5 },
                    {
                        yPercent: 5,
                        ease: 'none',
                        scrollTrigger: { trigger: element, start: 'top bottom', end: 'bottom top', scrub: true }
                    }
                )
            }
        })

        return () => media.revert()
    }, { scope: ref })

    return (
        <div ref={ref} className={cn('relative overflow-hidden bg-sand', className)}>
            <div ref={innerRef} className={cn('absolute inset-x-0', parallax ? '-inset-y-[7%]' : 'inset-y-0')}>
                <Image
                    src={src}
                    alt={alt}
                    fill
                    sizes={sizes}
                    priority={priority}
                    className={cn('object-cover', imgClassName)}
                />
            </div>

            {children}
        </div>
    )
}
